import { ScrollWrapper } from "@mdotm/shadcn/legacy/components";
import { type ReactNode, useCallback, useImperativeHandle, useLayoutEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
	type ImperativeHandleRefProps,
	type NodeOrFn,
	renderNodeOrFn,
	toClassName,
} from "@mdotm/shadcn/legacy/react-extensions";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";

export type VirtualListImperativeHandles = {
	/** Jump to the very bottom of the list. */
	scrollToBottom(): void;
	/** The scrollable DOM element, or `null` if the list is not mounted. */
	getScrollElement(): HTMLDivElement | null;
};

export type VirtualListProps<T> = {
	items: ArrayLike<T>;
	children: NodeOrFn<{ item: T; index: number }>;
	scrollToBottomChildren?: (props: { onClick(): void }) => ReactNode;
	keyProvider?(index: number): string | number;
	/**
	 * Fired after the scrollable content changes height — new/removed items or a
	 * row re-measuring. Lets a consumer implement behaviors like "follow the
	 * bottom while streaming" without baking that policy into the generic list.
	 */
	onContentResize?(): void;
} & ImperativeHandleRefProps<VirtualListImperativeHandles>;

export function VirtualList<T>({
	items,
	children,
	scrollToBottomChildren,
	handleRef,
	keyProvider,
	onContentResize,
}: VirtualListProps<T>): React.JSX.Element {
	const scrollableElementRef = useRef<HTMLDivElement | null>(null);
	const rowVirtualizer = useVirtualizer({
		getScrollElement: () => scrollableElementRef.current,
		count: items.length,
		estimateSize: () => 35,
		getItemKey: keyProvider,
	});

	const getScrollElement = useCallback(() => scrollableElementRef.current, []);
	const scrollToBottom = useCallback(() => {
		const el = scrollableElementRef.current;
		if (el) {
			// `instant` overrides the container's CSS `scroll-behavior: smooth`, so
			// following streamed content snaps to the bottom instead of visibly
			// animating (which glitches under rapid, back-to-back updates).
			el.scrollTo({ top: el.scrollHeight, behavior: "instant" });
		}
	}, []);
	useImperativeHandle(handleRef, () => ({ scrollToBottom, getScrollElement }), [scrollToBottom, getScrollElement]);

	// Report content-height changes (new/removed items, rows re-measuring) so the
	// consumer can drive its own scroll behavior. Read the callback via a ref so
	// `totalSize` stays the sole trigger and a changing callback identity doesn't
	// refire the effect.
	const totalSize = rowVirtualizer.getTotalSize();
	const onContentResizeRef = useUnsafeUpdatedRef(onContentResize);
	// biome-ignore lint/correctness/useExhaustiveDependencies: `totalSize` is intentionally the sole trigger; the callback is read live from the ref.
	useLayoutEffect(() => {
		onContentResizeRef.current?.();
	}, [totalSize]);
	return (
		<div className="relative z-0 flex-1 min-h-0 flex flex-col">
			<ScrollWrapper
				endShadow={({ scrollTillEnd }) =>
					scrollToBottomChildren && (
						<div
							className={toClassName({
								"absolute z-10 bottom-4 right-4 transition-opacity duration-150": true,
								// Hide instantly, but delay showing: a content jump that briefly
								// pushes us off the bottom before auto-follow re-pins (e.g. a tall
								// tool box appearing) shouldn't flash the button in and out.
								"opacity-0 delay-0": (scrollTillEnd ?? 0) < 10,
								"opacity-100 delay-300": (scrollTillEnd ?? 0) >= 10,
							})}
						>
							{scrollToBottomChildren({
								onClick: scrollToBottom,
							})}
						</div>
					)
				}
				className="flex-1"
				outerContainerAppearance={{ className: "flex-1 relative z-0" }}
				innerRef={scrollableElementRef}
			>
				<div
					style={{
						height: `${rowVirtualizer.getTotalSize()}px`,
						width: "100%",
						position: "relative",
					}}
				>
					{/* Only the visible items in the virtualizer, manually positioned to be in view */}
					{rowVirtualizer.getVirtualItems().map((virtualItem) => (
						<div
							key={virtualItem.key}
							data-index={virtualItem.index}
							ref={rowVirtualizer.measureElement}
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								// minHeight: `${virtualItem.size}px`,
								transform: `translateY(${virtualItem.start}px)`,
							}}
						>
							{renderNodeOrFn(children, {
								item: items[virtualItem.index],
								index: virtualItem.index,
							})}
						</div>
					))}
				</div>
			</ScrollWrapper>
		</div>
	);
}
